import {
  messagesSelector,
  PersonCapabilityType,
  personCapabilityTypes,
  personCapabilityTypesSelector,
  PersonEntity,
  rolesSelector,
  useGetPersonCapabilitiesQuery,
  useGetPersonNoticesQuery,
  useGetRolesByPersonQuery
} from "@ots-frontend/ots-data";
import {
  ActionIcon,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Loader,
  LoadingOverlay,
  Modal,
  ModalProps,
  Stack,
  Text,
  Title
} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ObjectMappingDataTableCard} from "../../card/ObjectMappingDataTableCard/ObjectMappingDataTableCard";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {CommentList} from "../../comment/CommentList/CommentList";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {IconEdit, IconMessagePlus} from "@tabler/icons-react";
import {CommentModal} from "../../comment/CommentModal/CommentModal";
import {useEntityCommentModal} from "../../comment/EntityCommentModal/useEntityCommentModal";
import {usePersonAccess} from "../../../hooks/usePersonAccess";
import {InputInfoTooltip} from "../../tooltip/InfoTooltip/InfoTooltip";
import {useDisclosure} from "@mantine/hooks";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {useRoleMutation} from "../../../hooks/useRoleMutation";
import {RoleMultiSelect} from "../../select/entity/RoleSelect/RoleMultiSelect";
import {PersonCapabilityDataTable} from "../../data-table/PersonCapabilityDataTable/PersonCapabilityDataTable";
import {usePersonMutation} from "../../../hooks/usePersonMutation";
import {PAGINATION_PAGE_SIZES} from "../../data-table/DataTable/usePagination";

const NoticesCard = (props: { person: PersonEntity }) => {
  const {person} = props

  const {t} = useTranslation(["common"])

  const selectMessages = useMemo(() => {
    return messagesSelector()
  }, []);

  const {messages, isLoading} = useGetPersonNoticesQuery(person.id ?? skipToken, {
    selectFromResult: (result) => ({
      messages: selectMessages(result),
      isLoading: result.isLoading,
    })
  });

  const {
    opened,
    handlers,
    onSubmit: onCommentSubmit,
    isCreating: isCommentCreating,
  } = useEntityCommentModal({
    entity: person,
    messageType: "User Notice",
  });

  const canEdit = usePersonAccess({person: person})

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>

      <Group position="apart" pb="sm">
        <Group spacing={"xs"}>
          <Title order={4}>
            {t("common.entity.person.profile.info.section.notices.label", "Notices")}
          </Title>
          <InputInfoTooltip
            label={t("common.entity.person.profile.info.section.notices.tooltip", "Notices are timed messages that display when driver starts a shift")}/>
        </Group>
        {canEdit &&
          <ActionIcon color="green" onClick={handlers.open}>
            <IconMessagePlus size={16}/>
          </ActionIcon>
        }
      </Group>

      <Divider/>

      <Grid>
        <Grid.Col span={12}>
          <CommentList
            comments={messages}
            isLoading={isLoading}
            empty={{
              show: true,
              message: t<string>("common.entity.person.profile.info.section.notices.empty", "No Notices"),
            }}/>
        </Grid.Col>
      </Grid>

      <CommentModal
        opened={opened}
        onClose={handlers.close}
        onSubmit={onCommentSubmit}
        loading={isCommentCreating}
        title={t<string>("common.entity.person.profile.info.section.notices.action.new", "New Notice")}/>
    </Card>
  )
}


export type PersonDetailsProps = {
  person: PersonEntity
}

export const PersonDetails = (props: PersonDetailsProps) => {
  const {person} = props;

  const {t} = useTranslation(["common"]);

  const [roleManagerOpen, roleManagerHandler] = useDisclosure(false);

  const selectRoles = useMemo(() => {
    return rolesSelector()
  }, []);

  const {roles, isRolesLoading} = useGetRolesByPersonQuery(person.id ?? skipToken, {
    selectFromResult: (result) => ({
      roles: selectRoles(result),
      isRolesLoading: result.isLoading,
    })
  });

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.person.info.section.details.label", "Details")}</Title>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.person.info.field.name.label", "Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {person.name}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.person.info.field.type.label", "Type")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {person.type && <FormattedDatabaseCode value={person.type}/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.person.info.field.roles.label", "Roles")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <Group spacing="xs">
                  {isRolesLoading && <Loader size="xs" color="gray"/>}
                  {roles.length === 0 ? (
                    <Text size="xs" color="gray">
                      No Roles
                    </Text>
                  ) : (
                    <>
                      {roles.map((r, i) => {
                        return (
                          <>
                            <EntityAnchor value={r} spacing="xs"/>
                            {i < roles.length - 1 && ","}
                          </>
                        )
                      })}
                    </>
                  )}
                  <HasAccess permissions={["person_edit"]}>
                    <ActionIcon onClick={() => roleManagerHandler.open()}>
                      <IconEdit size={16}/>
                    </ActionIcon>
                  </HasAccess>
                </Group>
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.person.info.field.company.label", "Company")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {person.companyId && <DefaultText value={person.companyName}/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.person.info.field.email.label", "Email")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {person.emailAddress}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.person.info.field.phonenumber.label", "Phone Number")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {person.phoneNumber}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.person.info.field.preferredlanguage.label", "Preferred Language")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {person.preferredLanguage && <FormattedDatabaseCode value={person.preferredLanguage}/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.person.info.field.username.label", "Username")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {person.userName}
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.person.info.field.usable.label", "Usable")}
              </Text>
              <Text ta="left" c={person.doNotUse ? "warning" : "dimmed"} fz="xs">
                {person.doNotUse ? t("common.shared.no", "No") : t("common.shared.yes", "Yes")}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>
      <Grid.Col span={6}>
        <NoticesCard person={person}/>
      </Grid.Col>
      <Grid.Col span={12}>
        <PersonCapabilityCard personId={person.id}/>
      </Grid.Col>
      <Grid.Col span={12}>
        <HasAccess permissions={["object_mapping_view"]}>
          <ObjectMappingDataTableCard entity={person}/>
        </HasAccess>
      </Grid.Col>

      <RoleManagerModal
        opened={roleManagerOpen}
        onClose={roleManagerHandler.close}
        person={person}
        assignedRoles={roles.map(r => r.id)}/>
    </Grid>
  );
};

export type RoleManagerModalProps = Omit<ModalProps, 'onSubmit' | 'title'> & {
  person: PersonEntity
  assignedRoles?: string[]
}

export const RoleManagerModal = (props: RoleManagerModalProps) => {
  const {person, assignedRoles, onClose, ...rest} = props;

  const {t} = useTranslation(["common"]);

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  useEffect(() => {
    if (assignedRoles) {
      setSelectedRoles(assignedRoles)
    }
  }, [assignedRoles])

  const {
    setRoles,
    isSettingRoles
  } = useRoleMutation()

  const onSaveClick = useCallback(() => {
    setRoles(selectedRoles, person.id, () => onClose())
  }, [setRoles, selectedRoles, person.id, onClose])

  return (
    <Modal
      {...rest}
      title={t<string>("common.entity.person.profile.rolemanager.title", "Manage Roles")}
      onClose={onClose}
    >
      <LoadingOverlay visible={isSettingRoles}/>
      <Stack spacing="md">
        <RoleMultiSelect
          label="Assigned Roles"
          placeholder="Select roles…"
          value={selectedRoles}
          onChange={setSelectedRoles}
          withinPortal={true}
          searchable
          clearable
        />

        <Group position="right">
          <Button variant="default" onClick={onClose}>
            {t("common.action.cancel", "Cancel", {ns: "common"})}
          </Button>
          <Button onClick={onSaveClick} loading={isSettingRoles}>
            {t("common.action.submit", "Submit", {ns: "common"})}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

const PersonCapabilityCard = (props: { personId: string }) => {
  const {personId} = props;

  const {t} = useTranslation(["common"]);

  const selectCapabilities = useMemo(() => {
    return personCapabilityTypesSelector();
  }, []);

  const {activeCapabilities, isActiveCapabilitiesLoading} = useGetPersonCapabilitiesQuery(personId, {
    selectFromResult: (result) => ({
      ...result,
      activeCapabilities: selectCapabilities(result),
      isActiveCapabilitiesLoading: result.isLoading,
    })
  });

  const {
    addCapabilities,
    removeCapabilities,
    isAddingCapabilities,
    isRemovingCapabilities,
  } = usePersonMutation()

  const isCapabilitiesLoading = useMemo(() => {
    return isActiveCapabilitiesLoading || isAddingCapabilities || isRemovingCapabilities
  }, [isAddingCapabilities, isRemovingCapabilities, isActiveCapabilitiesLoading])

  const toggleCapabilities = useCallback((cap: PersonCapabilityType) => {
    const caps: string[] = []
    caps.push(cap.code)

    const value = activeCapabilities.has(cap)
    if (value) {
      removeCapabilities(personId, {capabilities: caps}, undefined)
    } else {
      addCapabilities(personId, {capabilities: caps}, undefined)
    }
  }, [addCapabilities, removeCapabilities, personId, activeCapabilities])

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group position="apart" pb="sm">
        <Title order={4}>{
          t("common.entity.person.profile.info.section.capabilities.label", "Capabilities")}
        </Title>
      </Group>

      <Divider my="md"/>

      <PersonCapabilityDataTable
        permPrefix="person"
        selectedCapabilities={activeCapabilities}
        toggleCapability={toggleCapabilities}
        verticalSpacing="sm"
        withBorder
        records={personCapabilityTypes}
        recordsPerPageOptions={PAGINATION_PAGE_SIZES}
        fetching={isCapabilitiesLoading}
      />
    </Card>
  )
}

